import React, { useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Paper,
	Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
	ProjectEvaluationStatus,
	EvaluatorType
} from "../../../models/ProjectEvaluation";
import { UserType } from "../../../models/UserType";
import { User } from "../../../models/User";
import {
	useProjectEvaluations,
	useSubmitProjectEvaluation
} from "../../../hooks/useProjectEvaluations";
import { useProject } from "../../../hooks/useProject";
import { zoullooColors } from "../../../theme";
import HtmlEditor from "../../UI/HtmlEditor";
import StarRating from "./StarRating";
import EvaluationDisplay from "./EvaluationDisplay";
import EvaluationModerationPanel from "./EvaluationModerationPanel";

interface EvaluationTabProps {
	projectId: number;
	user: User;
	isCSM: boolean;
}

const EvaluationTab: React.FC<EvaluationTabProps> = ({ projectId, user, isCSM }) => {
	const { t } = useTranslation();
	const { data: evaluations = [], isLoading: isLoadingEvaluations } =
		useProjectEvaluations(projectId);
	const { data: project, isLoading: isLoadingProject } = useProject(projectId);
	const submitMutation = useSubmitProjectEvaluation();

	// Form state
	const [rating, setRating] = useState<number>(0);
	const [comment, setComment] = useState<string>("");
	const [consentGiven, setConsentGiven] = useState<boolean>(false);
	const [errors, setErrors] = useState<{
		rating?: string;
		comment?: string;
	}>({});

	const isHtmlContentEmpty = (html: string): boolean => {
		if (!html) return true;
		const text = html.replace(/<[^>]*>/g, "").trim();
		return text.length === 0;
	};

	const validate = (): boolean => {
		const newErrors: { rating?: string; comment?: string } = {};

		if (rating === 0) {
			newErrors.rating = t("evaluations.form.ratingRequired");
		}

		if (isHtmlContentEmpty(comment)) {
			newErrors.comment = t("evaluations.form.commentRequired");
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!validate() || !ownEvaluation) return;

		submitMutation.mutate({
			id: ownEvaluation.id,
			request: {
				rating,
				comment,
				consentGiven
			}
		});
	};

	if (isLoadingEvaluations || isLoadingProject) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	// Determine own evaluation (matched by assignedToUserId)
	const ownEvaluation = evaluations.find(
		e => e.assignedToUserId === user.id
	);

	// For client view: freelancer's evaluation (published)
	const freelancerEvaluation = evaluations.find(
		e =>
			e.assignedToUserId !== user.id &&
			e.evaluatorType === EvaluatorType.Freelancer &&
			e.status === ProjectEvaluationStatus.Published
	);

	// For client view: other clients' evaluations (published, not own)
	const colleagueEvaluations = evaluations.filter(
		e =>
			e.assignedToUserId !== user.id &&
			e.evaluatorType === EvaluatorType.Client &&
			e.status === ProjectEvaluationStatus.Published
	);

	// For freelancer view: all client evaluations (published)
	const clientEvaluations = evaluations.filter(
		e =>
			e.assignedToUserId !== user.id &&
			e.evaluatorType === EvaluatorType.Client &&
			e.status === ProjectEvaluationStatus.Published
	);

	// Extract names from evaluations data
	const freelancerFirstName =
		evaluations.find(e => e.freelancerFirstName)?.freelancerFirstName || "";
	const freelancerLastName =
		evaluations.find(e => e.freelancerLastName)?.freelancerLastName || "";
	const freelancerFullName = `${freelancerFirstName} ${freelancerLastName}`.trim();
	const companyName =
		evaluations.find(e => e.evaluatorCompanyName)?.evaluatorCompanyName || "";

	const isClient = user.type === UserType.Client;
	const hasOwnEvaluation = ownEvaluation != null;
	const isPending = ownEvaluation?.status === ProjectEvaluationStatus.Pending;
	const isSubmitted =
		ownEvaluation?.status === ProjectEvaluationStatus.Submitted ||
		ownEvaluation?.status === ProjectEvaluationStatus.Published ||
		ownEvaluation?.status === ProjectEvaluationStatus.Rejected;

	// ─── CSM VIEW ───────────────────────────────────────────────
	if (isCSM) {
		return (
			<Box>
				<Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
					{t("evaluations.allEvaluations")}
				</Typography>

				{evaluations.map(evaluation => (
					<Paper key={evaluation.id} sx={{ p: 3, mb: 3 }}>
						<EvaluationDisplay
							evaluation={evaluation}
							variant="simple"
							showConsent
						/>
						{evaluation.status !== ProjectEvaluationStatus.Pending && (
							<EvaluationModerationPanel evaluation={evaluation} />
						)}
					</Paper>
				))}

				{evaluations.length === 0 && (
					<Typography variant="body1" color="text.secondary">
						{t("evaluations.noEvaluations")}
					</Typography>
				)}
			</Box>
		);
	}

	// ─── CLIENT / FREELANCER VIEW ───────────────────────────────
	const projectName = project?.name || "";
	const projectNumber = project?.projectNumber || "";

	// Section title for own evaluation
	const ownSectionTitle = isClient
		? t("evaluations.yourEvaluationOfFreelancer", {
				firstName: freelancerFirstName,
				lastName: freelancerLastName
			})
		: t("evaluations.yourEvaluationOfCompany", { companyName });


	// Help text for the form
	const helpText = isClient
		? t("evaluations.helpTextClient")
		: t("evaluations.helpTextFreelancer");

	// Consent label
	const consentLabel = isClient
		? t("evaluations.consentClient")
		: t("evaluations.consentFreelancer");

	// Page title
	const pageTitle = isClient
		? t("evaluations.evaluateYourMissionClient", { projectName })
		: t("evaluations.evaluateYourMissionFreelancer", { projectName });

	return (
		<Box>
			{/* Page title */}
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
				{pageTitle}
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				{projectNumber}
			</Typography>

			{/* ─── Own evaluation section ─── */}
			{hasOwnEvaluation && (
				<Paper sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
						{ownSectionTitle}
					</Typography>

					{isPending && (
						<>
							{/* Star rating */}
							<Box sx={{ mb: 3 }}>
								<StarRating value={rating} onChange={setRating} size="large" />
								{errors.rating && (
									<Typography
										variant="body2"
										color="error"
										sx={{ mt: 0.5 }}
									>
										{errors.rating}
									</Typography>
								)}
							</Box>

							{/* Help text */}
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 3, whiteSpace: "pre-line" }}
							>
								{helpText}
							</Typography>

							{/* WYSIWYG editor */}
							<Box sx={{ mb: 3 }}>
								<HtmlEditor
									content={comment}
									onUpdate={({ editor }) => setComment(editor.getHTML())}
									placeholder={t("evaluations.form.commentPlaceholder")}
								/>
								{errors.comment && (
									<Typography
										variant="body2"
										color="error"
										sx={{ mt: 0.5 }}
									>
										{errors.comment}
									</Typography>
								)}
							</Box>

							{/* Consent checkbox */}
							<Box sx={{ mb: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={consentGiven}
											onChange={e =>
												setConsentGiven(e.target.checked)
											}
											color="secondary"
										/>
									}
									label={
										<Typography variant="body2">
											{consentLabel}
										</Typography>
									}
									sx={{ alignItems: "flex-start", mr: 0 }}
								/>
							</Box>

							{/* Submit button */}
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button
									variant="contained"
									onClick={handleSubmit}
									disabled={submitMutation.isPending}
									sx={{
										backgroundColor: zoullooColors.secondary.main,
										"&:hover": {
											backgroundColor: zoullooColors.secondary.dark
										}
									}}
								>
									{submitMutation.isPending
										? t("common.submitting")
										: t("evaluations.form.submitMyEvaluation")}
								</Button>
							</Box>
						</>
					)}

					{isSubmitted && ownEvaluation && (
						<>
							{ownEvaluation.status ===
								ProjectEvaluationStatus.Submitted && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 2, fontStyle: "italic" }}
								>
									{t("evaluations.awaitingModeration")}
								</Typography>
							)}
							<EvaluationDisplay evaluation={ownEvaluation} variant="simple" />
						</>
					)}
				</Paper>
			)}

			{/* ─── CLIENT VIEW: Freelancer evaluation + Colleague evaluations ─── */}
			{isClient && (
				<>
					{/* Freelancer's evaluation */}
					<Paper sx={{ p: 3, mb: 3 }}>
						<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
							{t("evaluations.freelancerEvaluationOfCollaboration", {
								firstName: freelancerFirstName,
								lastName: freelancerLastName
							})}
						</Typography>

						{freelancerEvaluation ? (
							<EvaluationDisplay evaluation={freelancerEvaluation} variant="simple" />
						) : (
							<Typography variant="body2" color="text.secondary">
								{t("evaluations.freelancerNotYetEvaluated", {
									freelancerName: freelancerFullName
								})}
							</Typography>
						)}
					</Paper>

					{/* Colleague evaluations */}
					{colleagueEvaluations.length > 0 && (
						<Paper sx={{ p: 3 }}>
							<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
								{t("evaluations.colleagueEvaluations", { companyName })}
							</Typography>

							{colleagueEvaluations.map(evaluation => (
								<Box key={evaluation.id} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
									<EvaluationDisplay evaluation={evaluation} variant="simple" />
								</Box>
							))}
						</Paper>
					)}
				</>
			)}

			{/* ─── FREELANCER VIEW: Client evaluations ─── */}
			{!isClient && (
				<Paper sx={{ p: 3 }}>
					<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
						{t("evaluations.clientEvaluationOfCollaboration", { companyName })}
					</Typography>

					{clientEvaluations.length > 0 ? (
						clientEvaluations.map(evaluation => (
							<Box key={evaluation.id} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
								<EvaluationDisplay evaluation={evaluation} variant="simple" />
							</Box>
						))
					) : (
						<Typography variant="body2" color="text.secondary">
							{t("evaluations.clientNotYetEvaluated", { companyName })}
						</Typography>
					)}
				</Paper>
			)}
		</Box>
	);
};

export default EvaluationTab;
